import { Component, OnInit, HostBinding, HostListener, ViewChild, ElementRef } from '@angular/core';
import { routeNestedSlider } from 'src/app/route-animations';
import { BaseComponent } from 'src/app/shared/components/base.component';
import { AuthenticationService } from 'src/core/http/authentication.service';
import { Router, ActivatedRoute } from '@angular/router';
import { take, finalize, map, catchError, takeUntil } from 'rxjs/operators';
import { AppService } from 'src/core/services/app-service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NotificationsService } from 'angular2-notifications';
import { NestedAlertSummaryService } from 'src/core/services/alert-summary.service';
import { CustomValidators } from 'src/app/shared/custom-validators';
import { ProviderService } from 'src/core/http/provider.service';
import { MessageService } from 'src/core/http/message.service';
import { HttpEventType, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import * as ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { ChangeEvent } from '@ckeditor/ckeditor5-angular';

@Component({
  selector: 'app-message-add',
  templateUrl: './message-add.component.html',
  styleUrls: ['./message-add.component.scss'],
  animations: [
    routeNestedSlider
  ]
})

export class MessageAddComponent extends BaseComponent implements OnInit {
  @HostBinding('@routeNestedAnimations') routerTransition = 'slideinAnimTransition';
  depth: number;

  customRecipientsVisibleYN = false;

  files: any[] = [];

  @ViewChild('fileDropRef', { static: true }) fileDropRef: ElementRef;

  public form: FormGroup;
  formSubmitted = false;

  public richEditor = ClassicEditor;
  // https://ckeditor.com/ckeditor-5/online-builder/
  public richEditorConfig = {
    placeholder: 'Type the content here!',
    toolbar: {
      items: [
        'fontFamily',
        'fontSize',
        'fontColor',
        'fontBackgroundColor',
        '|',
        'bold',
        'italic',
        'underline',
        'strikethrough',
        '|',
        'alignment',
        'indent',
        'outdent',
        '|',
        'bulletedList',
        'numberedList',
        'link',
        'blockQuote'
      ]
    },
  };

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private appService: AppService,
    private formBuilder: FormBuilder,
    private messageService: MessageService,
    private providerService: ProviderService,
    private snackbarService: NotificationsService,
    protected authenticationService: AuthenticationService,
    private nestedAlertSummaryService: NestedAlertSummaryService,
  ) {
    super(authenticationService);
  }


  ngOnInit() {
    this.createForm();

    this.route.data
    .pipe(take(1)/*takeUntil(this.unsubscribe)*/)
    .subscribe((data) => {
      this.depth = data.depth;
    });

    setTimeout(() => {
      this.appService.setBlockScrolling(true);
    });
  }


  back() {
    this.router.navigate(['./'], { relativeTo: this.route.parent });
    // this.router.navigate(['../']);
  }


  onEscapeKeydown(event) {
    this.back();
  }


  private createForm() {
    this.form  = this.formBuilder.group({
      recipientType:  [true, []],
      recipientGroup: this.formBuilder.group({
        inputControl: [''],
        selection: [''/*, CustomValidators.requiredNotEmptyList()*/],
      }),
      highImportanceYN:  [false, []],
      MessageSubject: ['', [Validators.required, Validators.maxLength(65)]],
      MessageBody: ['', [Validators.required/*, Validators.maxLength(500)*/]],
    });

    this.form.get('recipientType').valueChanges.subscribe(allYN => {
      const recipientGroupCtrl = this.form.get('recipientGroup');
      const recipientGroupSelectionCtrl = recipientGroupCtrl.get('selection');
      if (!allYN) {
        this.customRecipientsVisibleYN = true;
        recipientGroupSelectionCtrl.setValidators([CustomValidators.requiredNotEmptyList()]);
        recipientGroupCtrl.markAsUntouched();
        recipientGroupCtrl.setErrors(null);
      } else {
        this.customRecipientsVisibleYN = false;
        recipientGroupSelectionCtrl.setValidators(null);
        // Set recipientGroup to empty
        this.form.patchValue({
          recipientGroup: {
            inputControl: '',
            selection: []
          }
        });
      }
      recipientGroupCtrl.updateValueAndValidity();
      recipientGroupSelectionCtrl.updateValueAndValidity();
    });
  }


  get formControls() { return this.form.controls; }



  getProviders = (currentIndex: number, pageSize: number, query: string) => {
    return this.providerService.getProviders(currentIndex, pageSize, 'LastName ASC', query, null, 'active', true);
  }


  recipientNameFormat(recipient: any) {
    return `${recipient.LastName} ${recipient.FirstName}` + (recipient.AuthUserInitial && recipient.AuthUserInitial.length > 0 ? ` (${recipient.AuthUserInitial})` : '');
  }


  onSubmit() {
    this.formSubmitted = true;
    if (this.form.valid) {
      const uploadingFiles = this.files.filter(f => f.progress < 100 && f.inProgress);
      if (!uploadingFiles || uploadingFiles.length === 0) {
        this.appService.setIsLoading(true);
        const formValues = this.form.getRawValue();
        const dataToSend = {
          Subject: formValues.MessageSubject,
          Body: formValues.MessageBody,
          MessageToList: this.customRecipientsVisibleYN ? formValues.recipientGroup.selection.map(r => r.AuthUserId) : [],
          Priority: formValues.highImportanceYN ? 2060003 : 2060001,
          Attachments: this.files && this.files.length > 0 ? this.files.filter(f => f.progress === 100 && !(f.inProgress === true) && !(f.errorYN === true)).map(r => r.Identifier) : []
        };
        this.messageService.postMessage(dataToSend)
        .pipe(
          take(1), /*takeUntil(this.unsubscribe)*/
          finalize(() => { this.appService.setIsLoading(false); } )
        )
        .subscribe(
          (rawResult: any) => {
            if (rawResult && 'State' in rawResult && rawResult.State) {
              const toast = this.snackbarService.success('Message', 'posted successfully!');
              this.back();
              this.appService.setRefreshMainViewYN(true);
            } else {
              if (rawResult && 'Messages' in rawResult && rawResult.Messages.length > 0) {
                this.nestedAlertSummaryService.error(rawResult.Messages);
              } else {
                this.nestedAlertSummaryService.error('An Error occured');
              }
            }
          },
          error => console.log('Error:', error),
        );
      } else {
        const toast = this.snackbarService.warn('Uploading files', 'please wait and try again when it is finished');
      }
    } else {
      CustomValidators.validateAllFormFields(this.form);
    }
  }




  /**
   * on file drop handler
   */
  onFileDropped($event) {
    this.prepareFilesList($event);
  }


  /**
   * handle file from browsing
   */
  fileBrowseHandler(files) {
    this.prepareFilesList(files);
  }


  /**
   * Delete file from files list
   * @param index (File index)
   */
  deleteFile(index: number) {
    this.files.splice(index, 1);
  }



  /**
   * Convert Files list to normal array list
   * @param files (Files List)
   */
  prepareFilesList(files: Array<any>) {
    for (const file of files) {
      // if (file.size <= 1 * 1024 * 1024) {
        // console.log('FILE', file);
        file.progress = 0;
        this.files.push(file);
        this.uploadFile(file);
      // } else {
      //   const toast = this.snackbarService.error('<i>' + file.name + '</i>', 'exceeds max file size (1MB).');
      //   this.files.push(file);
      //   file.errorYN = true;
      // }
    }
    this.fileDropRef.nativeElement.value = '';
    // this.uploadFilesSimulator(0);
  }


  /**
   * format bytes
   * @param bytes (File size in bytes)
   * @param decimals (Decimals point)
   */
  formatBytes(bytes, decimals = 2) {
    if (bytes === 0) {
      return '0 Bytes';
    }
    const k = 1024;
    const dm = decimals <= 0 ? 0 : decimals || 2;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }



  uploadFile(fileToUpload: any) {
    fileToUpload.inProgress = true;
    const formData = new FormData();
    formData.append('file', fileToUpload, fileToUpload.name); // IMPORTANT: 'file' (first parameter) must match parameter name in controller's action
    this.messageService
      .uploadAttachment(formData)
      .pipe(
        takeUntil(this.unsubscribe),
        map(event => {
          switch (event.type) {
            case HttpEventType.UploadProgress:
              fileToUpload.progress = Math.round(event.loaded * 100 / event.total);
              break;
            case HttpEventType.Response:
              return event;
          }
        }),
        finalize(() => { fileToUpload.inProgress = false; } )
        // catchError((error: HttpErrorResponse) => {
        //   fileToUpload.inProgress = false;
        //   return of(`${fileToUpload.data.name} upload failed.`);
        // })
        )
        .subscribe((event: any) => {
          if (typeof (event) === 'object') {
            const rawResult = event.body;
            if (rawResult && 'State' in rawResult && rawResult.State) {
              fileToUpload.Identifier = rawResult.Data;
            } else {
              if (rawResult && 'Messages' in rawResult && rawResult.Messages.length > 0) {
                this.nestedAlertSummaryService.error(rawResult.Messages);
              } else {
                this.nestedAlertSummaryService.error('An Error occured');
              }
            }
          }
        },
        error => {
          fileToUpload.errorYN = true;
          console.log('Error:', error);
        });
  }

  // onRichEditorChange( { editor }: ChangeEvent ) {
  //   const data = editor.getData();
  // }

}
